import { DeleteOutlined, EditFilled, SearchOutlined } from "@ant-design/icons";
import {
  Button,
  Col,
  Pagination,
  Row,
  Space,
  Table,
  TabsProps,
  TableProps,
} from "antd";
import type { Key, RefObject } from "react";
import dayjs from "dayjs";
import { ArrowDownCircle, PlusIcon } from "../../../assets/icon";
import {
  Tabs,
  Input,
  TableActionButtons,
  buildColumnsWithHeaderFilters,
} from "../../../Components";
import { checkPermissions } from "../../../utils/checkPermissions";
import { decorateEditableColumns } from "../../../shared/helpers/editableColumns";
import {
  canApplyFreightVersion,
  getFreightVersionOrderCount,
} from "../../../shared/helpers/freightVersion";
import styles from "../FreightCosts.module.scss";

type FreightCostListTabProps = {
  activeTabs: string;
  activeVersion: string | null;
  columns: any[];
  dataSource: any[];
  dataVersion: any[];
  editIndex: any[];
  features: any;
  filters: any;
  listVendor: any[];
  onAddRow: () => void;
  onActionCancel: (index: string | number) => void;
  onActionSaveFreightCost: (index: string | number, rowIndex: number) => void;
  isSavePending: (index: string | number) => boolean;
  isVersionApplyPending: (id: string) => boolean;
  onActionTable: (action: string, record: any, index: string) => void;
  onBulkDelete: () => void;
  onChangeActiveTabs: (tab: string) => void;
  onGoToGroup: () => void;
  onExportVersion: (id: string, name: string) => void;
  onApplyVersion: (record: any) => void;
  onPaginationChange: (page: number, pageSize?: number) => void;
  onRowSelectionChange: (selectedKeys: Key[]) => void;
  onSetActiveVersion: (id: string) => void;
  onSearchContractorChange: (value: string) => void;
  pagination: {
    total: number;
    current: number;
    pageSize: number;
  };
  rightContainerRef: RefObject<HTMLDivElement>;
  rightHeight?: number;
  selectedRowKeys: Key[];
  setFilters: (value: any) => void;
  tabItems: TabsProps["items"];
  valueSearchContractor: string;
  onUpdateRow: (key: string, value: any, index: string | number) => void;
};

const FreightCostListTab = ({
  activeTabs,
  activeVersion,
  columns,
  dataSource,
  dataVersion,
  editIndex,
  features,
  filters,
  listVendor,
  onAddRow,
  onActionCancel,
  onActionSaveFreightCost,
  isSavePending,
  isVersionApplyPending,
  onActionTable,
  onBulkDelete,
  onChangeActiveTabs,
  onExportVersion,
  onApplyVersion,
  onGoToGroup,
  onPaginationChange,
  onRowSelectionChange,
  onSetActiveVersion,
  onSearchContractorChange,
  pagination,
  rightContainerRef,
  rightHeight,
  selectedRowKeys,
  setFilters,
  tabItems,
  valueSearchContractor,
  onUpdateRow,
}: FreightCostListTabProps) => {
  const canEditFreightCost = checkPermissions(
    "CONTRACTOR_FREIGHT",
    "edit",
    features,
  );
  const columnsTableVersion: TableProps<any>["columns"] = [
    {
      title: "#",
      dataIndex: "id",
      key: "id",
      width: 50,
      fixed: "left",
      hidden: false,
      render: (_row: any, _record: any, index: number) => index + 1,
    },
    {
      title: "Mã phiên bản",
      dataIndex: "name",
      fixed: false,
      hidden: false,
    },
    {
      title: "Ghi chú",
      dataIndex: "note",
      fixed: false,
      hidden: false,
    },
    {
      title: "Ngày hiệu lực",
      dataIndex: "validFrom",
      fixed: false,
      hidden: false,
      render: (row: any) => dayjs(row).format("DD/MM/YYYY"),
    },
    {
      title: "Đơn hàng bị ảnh hưởng",
      dataIndex: "affectedOrderCount",
      align: "center",
      width: 170,
      render: (value: unknown) => getFreightVersionOrderCount(value),
    },
    {
      title: "Đơn hàng chưa áp dụng",
      dataIndex: "pendingOrderCount",
      align: "center",
      width: 180,
      render: (value: unknown) => {
        const pendingOrderCount = getFreightVersionOrderCount(value);

        return (
          <span
            style={{
              color: pendingOrderCount > 0 ? "#ff4d4f" : undefined,
              fontWeight: pendingOrderCount > 0 ? 600 : undefined,
            }}
          >
            {pendingOrderCount}
          </span>
        );
      },
    },
    {
      title: "",
      key: "action",
      fixed: true,
      width: 220,
      render: (_: any, record: any) => (
        <Space size={8}>
          <Button
            icon={<ArrowDownCircle />}
            onClick={() => {
              onExportVersion(record._id, record.name);
            }}
            disabled={
              !checkPermissions("CONTRACTOR_FREIGHT", "export", features)
            }
          />
          {canApplyFreightVersion(record, canEditFreightCost) && (
            <Button
              type="primary"
              loading={isVersionApplyPending(record._id)}
              disabled={isVersionApplyPending(record._id)}
              onClick={() => onApplyVersion(record)}
            >
              Áp dụng giá mới
            </Button>
          )}
        </Space>
      ),
    },
  ];
  const renderColumns = buildColumnsWithHeaderFilters(
    [
      ...decorateEditableColumns(columns, {
        editIndex,
        getDataSource: () => dataSource,
        onUpdate: (dataIndex, value, rowId) =>
          onUpdateRow(dataIndex, value, rowId),
      }),
      {
        title: "",
        key: "action",
        width: 60,
        fixed: "right",
        render: (_: any, record: any, index: number) => {
          const savePending = isSavePending(record._id);
          const saveAction = record.status === "new" ? "add" : "edit";
          const canSave = checkPermissions(
            "CONTRACTOR_FREIGHT",
            saveAction,
            features,
          );
          return editIndex.find((ind: any) => ind.index === record._id) ? (
            <div>
              <Button
                loading={savePending}
                disabled={savePending || !canSave}
                onClick={() => {
                  onActionSaveFreightCost(record._id, index);
                }}
              >
                {"\u004c\u01b0u"}
              </Button>{" "}
              <Button
                disabled={savePending}
                onClick={() => {
                  onActionCancel(record._id);
                }}
              >
                {"H\u1ee7y"}
              </Button>
            </div>
          ) : (
            <TableActionButtons
              items={[
                {
                  key: "edit",
                  label: "Chỉnh sửa",
                  icon: <EditFilled />,
                  tone: "edit",
                  disabled: !checkPermissions(
                    "CONTRACTOR_FREIGHT",
                    "edit",
                    features,
                  ),
                  onClick: () => onActionTable("edit", record, record._id),
                },
                {
                  key: "delete",
                  label: "Xoá",
                  icon: <DeleteOutlined />,
                  tone: "delete",
                  danger: true,
                  disabled: !checkPermissions(
                    "CONTRACTOR_FREIGHT",
                    "delete",
                    features,
                  ),
                  onClick: () => onActionTable("delete", record, record._id),
                },
              ]}
            />
          );
        },
      },
    ],
    filters,
    setFilters,
  );

  return (
    <div>
      <div>
        <Table
          columns={columnsTableVersion}
          dataSource={dataVersion}
          pagination={{ pageSize: 3 }}
          size="small"
          bordered
          scroll={{ x: "max-content" }}
          onRow={(record: any) => {
            return {
              onDoubleClick: () => {
                onSetActiveVersion(record._id);
              },
            };
          }}
          rowClassName={(record: any) =>
            record._id === activeVersion ? styles.HightLightRow : ""
          }
          rowKey={(record) => String(record._id)}
        />
        <Row align="middle" justify="space-between">
          <Tabs
            items={tabItems}
            activeKey={activeTabs}
            onChange={onChangeActiveTabs}
          />
          {selectedRowKeys.length > 0 && (
            <Button
              danger
              type="primary"
              onClick={onBulkDelete}
              disabled={!checkPermissions("CONTRACTOR_FREIGHT", "delete", features)}
            >
              Xóa
            </Button>
          )}
        </Row>
        <Row className={styles.MainRow} gutter={12} align="stretch">
          {activeTabs !== "1" && (
            <Col span={3}>
              <div
                className={styles.ListVendor}
                style={{ height: rightHeight ? `${rightHeight - 45}px` : undefined }}
              >
                <Input
                  prefix={<SearchOutlined />}
                  onChange={onSearchContractorChange}
                  placeholder={`${listVendor.length} nhà thầu`}
                  shaperound={true}
                />
                <div className={styles.WarpListVendor}>
                  {listVendor.length > 0 &&
                    listVendor
                      .filter((item: any) =>
                        item.shortName.toLowerCase().includes(valueSearchContractor),
                      )
                      .map((vendor: any) => {
                        return (
                          <div key={vendor._id} className={styles.Vendor}>
                            {vendor.shortName}
                          </div>
                        );
                      })}
                </div>
                <Button
                  style={{ padding: "13px 28px" }}
                  type="primary"
                  onClick={onGoToGroup}
                  disabled={
                    !checkPermissions("CONTRACTOR_FREIGHT_GROUP", "view", features)
                  }
                >
                  Quản lý thầu
                </Button>
              </div>
            </Col>
          )}
          <Col span={activeTabs === "1" ? 24 : 21} className={styles.RightCol}>
            <div ref={rightContainerRef}>
              <Table
                size="small"
                dataSource={dataSource}
                columns={renderColumns}
                scroll={{ x: "max-content" }}
                bordered
                style={{ width: "100%" }}
                pagination={false}
                rowSelection={{
                  selectedRowKeys,
                  onChange: onRowSelectionChange,
                }}
              />
              <Button
                className={styles.ButtonPlus}
                icon={<PlusIcon />}
                onClick={onAddRow}
                disabled={
                  !checkPermissions("CONTRACTOR_FREIGHT", "add", features)
                }
              >
                Thêm mới
              </Button>
              <Pagination
                total={pagination.total}
                current={pagination.current}
                pageSize={pagination.pageSize}
                onChange={onPaginationChange}
                showSizeChanger
                align="end"
                className={styles.Pagination}
              />
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export { FreightCostListTab };

